import React, { useState, useEffect, useRef } from "react";
import { ChatBubbleLeftRightIcon, XMarkIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { supportAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { v4 as uuidv4 } from "uuid";

interface Message {
  id: string;
  sender: string;
  message: string;
  created_at: string;
}

export default function LiveChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionId = useRef(localStorage.getItem("chat_session") || uuidv4());

  useEffect(() => {
    localStorage.setItem("chat_session", sessionId.current);
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await supportAPI.getChatHistory(sessionId.current);
      if (res.data.length > 0) {
        setMessages(res.data);
      } else {
        setMessages([{
          id: "welcome",
          sender: "bot",
          message: "Hi! Welcome to Kushi Fashions. How can I help you today? You can ask about orders, returns, sizes, or delivery.",
          created_at: new Date().toISOString(),
        }]);
      }
    } catch {
      setMessages([{
        id: "welcome",
        sender: "bot",
        message: "Hi! Welcome to Kushi Fashions. How can I help you today?",
        created_at: new Date().toISOString(),
      }]);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setInput("");

    const userMsg: Message = {
      id: Date.now().toString(),
      sender: "user",
      message: msg,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);

    try {
      const res = await supportAPI.sendChatMessage({
        session_id: sessionId.current,
        message: msg,
      });
      setMessages((prev) => [...prev, res.data]);
    } catch {
      setMessages((prev) => [...prev, {
        id: Date.now().toString(),
        sender: "bot",
        message: "Sorry, I couldn't process your message. Please try again or create a support ticket.",
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <>
      {/* Chat bubble */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-primary-600 text-white rounded-full p-4 shadow-lg hover:bg-primary-700 transition-colors z-50"
        aria-label="Open chat"
      >
        <ChatBubbleLeftRightIcon className="h-6 w-6" />
      </button>

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-20 right-6 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 max-h-[70vh]">
          {/* Header */}
          <div className="bg-primary-600 text-white rounded-t-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-primary-400 rounded-full flex items-center justify-center text-lg">👗</div>
              <div>
                <p className="font-semibold text-sm">Kushi Support</p>
                <p className="text-xs text-primary-200">Typically replies instantly</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-primary-500 p-1 rounded-full">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-48">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                    msg.sender === "user"
                      ? "bg-primary-600 text-white rounded-br-sm"
                      : "bg-gray-100 text-gray-800 rounded-bl-sm"
                  }`}
                >
                  {msg.message}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl px-4 py-2 text-sm rounded-bl-sm">
                  <span className="flex gap-1">
                    <span className="animate-bounce">•</span>
                    <span className="animate-bounce" style={{ animationDelay: "0.1s" }}>•</span>
                    <span className="animate-bounce" style={{ animationDelay: "0.2s" }}>•</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick replies */}
          <div className="px-4 pb-2 flex gap-2 flex-wrap">
            {["Return policy", "Track order", "Sizes"].map((q) => (
              <button
                key={q}
                onClick={() => { setInput(q); }}
                className="text-xs bg-primary-50 text-primary-700 rounded-full px-3 py-1 hover:bg-primary-100 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-100 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Type a message..."
              className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="bg-primary-600 text-white rounded-full p-2 hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
